import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import updateObjectInArray from '@afosto/utils/array/updateObjectInArray';
import { getFilterOperators } from './utils';

/**
 * Return a filter by key.
 * @param {string} key
 */
const getFilter = key => ({ filters }) => findObjectInArray(filters, 'key', key);

/**
 * Return a fixed filter.
 * @param {string} key
 * @return {object|undefined}
 */
const getFixedFilter = key => ({ fixedFilters }) => findObjectInArray(fixedFilters, 'key', key);

/**
 * Return an active filter.
 * @param {string} key
 * @return {object|undefined}
 */
const getActiveFilter = key => ({ activeFilters }) => findObjectInArray(activeFilters, 'key', key);

/**
 * Add selection to active filters.
 * @param {object} activeFilter
 */
const addActiveFilter = activeFilter => async ({
  activeFilters,
  setActiveFilters,
  onAddActiveFilter,
}) => {
  try {
    const newActiveFilters = [...activeFilters, activeFilter];

    if (onAddActiveFilter) {
      await onAddActiveFilter(activeFilter, newActiveFilters);
    }

    setActiveFilters(newActiveFilters);
  } catch (err) {
    // Do something with error.
  }
};

/**
 * Remove an active filter.
 * @param {string} filterKey
 */
const removeActiveFilter = filterKey => async ({
  activeFilters,
  setActiveFilters,
  onRemoveActiveFilter,
}) => {
  try {
    const activeFilter = findObjectInArray(activeFilters, 'key', filterKey);

    if (!activeFilter) {
      return;
    }

    const newActiveFilters = activeFilters.filter(filter => filter.key !== filterKey);

    if (onRemoveActiveFilter) {
      await onRemoveActiveFilter(activeFilter, newActiveFilters);
    }

    setActiveFilters(newActiveFilters);
  } catch (err) {
    // Do something with error.
  }
};

/**
 * Update an active filter.
 * @param {object} activeFilter
 */
const updateActiveFilter = activeFilter => async ({
  activeFilters,
  setActiveFilters,
  onUpdateActiveFilter,
}) => {
  try {
    const newActiveFilters = updateObjectInArray(activeFilters, activeFilter, 'key');

    if (onUpdateActiveFilter) {
      await onUpdateActiveFilter(activeFilter, newActiveFilters);
    }

    setActiveFilters(newActiveFilters);
  } catch (err) {
    // Do something with error.
  }
};

/**
 * Clear all active filters.
 */
const clearActiveFilters = () => async ({ setActiveFilters, onClearActiveFilters }) => {
  try {
    if (onClearActiveFilters) {
      await onClearActiveFilters();
    }

    setActiveFilters([]);
  } catch (err) {
    // Do something with error.
  }
};

/**
 * Clear the filter selection.
 */
const clearFilterSelection = () => ({ setFilterSelection }) => {
  setFilterSelection(null);
};

/**
 * Load filter selection for an active filter key.
 * @param {string} filterKey
 * @returns {Function}
 */
const loadFilterSelection = filterKey => async ({
  filters,
  activeFilters,
  setFilterSelection,
  loadFilterOptions,
}) => {
  try {
    const activeFilter = findObjectInArray(activeFilters, 'key', filterKey);

    if (!activeFilter) {
      return;
    }

    const filter = findObjectInArray(filters, 'key', filterKey);

    if (!filter) {
      return;
    }

    if (loadFilterOptions) {
      const filterOptions = await loadFilterOptions(filter);
      setFilterSelection({
        ...activeFilter,
        filterOptions,
        currentKey: activeFilter.key,
        isNew: false,
      });
    } else {
      setFilterSelection({
        ...activeFilter,
        filterOptions: filter.filterOptions || [],
        currentKey: activeFilter.key,
        isNew: false,
      });
    }
  } catch (err) {
    // Do something with error.
  }
};

/**
 * Save the filter selection.
 */
const saveFilterSelection = currentFilterKey => state => {
  const { filterSelection, setFilterSelection, activeFilters } = state;
  const { key, operator, value } = filterSelection;
  const { isNew, ...otherProps } = filterSelection;
  const filteredValue = Array.isArray(value) ? value.filter(option => option) : [];
  const invalidValue =
    !value ||
    value === '' ||
    value.length === 0 ||
    (operator === 'between' && filteredValue.length !== 2);

  if (!key || !operator || (invalidValue && operator !== 'missing')) {
    return;
  }

  let updatedActiveFilters = [...activeFilters];

  if (currentFilterKey && currentFilterKey !== key) {
    updatedActiveFilters = updatedActiveFilters.filter(
      activeFilter => activeFilter.key !== currentFilterKey,
    );
  }

  const activeFilter = getActiveFilter(key)({ ...state, activeFilters: updatedActiveFilters });

  if (activeFilter) {
    updateActiveFilter(otherProps)({ ...state, activeFilters: updatedActiveFilters });
  } else {
    addActiveFilter(otherProps)({ ...state, activeFilters: updatedActiveFilters });
  }

  setFilterSelection(null);
};

/**
 * Return the available (non-active) filters.
 * @returns {function({filters: *, activeFilters?: *}): *}
 */
const getAvailableFilters = () => ({ filterSelection, filters, activeFilters, fixedFilters }) =>
  filters.filter(filter => {
    const isActiveFilter = (activeFilters ?? []).some(
      activeFilter => activeFilter.key === filter.key,
    );
    const isCurrentSelection = filterSelection && filterSelection.currentKey === filter.key;
    const isFixedFilter = (fixedFilters ?? []).some(fixedFilter => fixedFilter.key === filter.key);

    return !isFixedFilter && (!isActiveFilter || isCurrentSelection);
  });

/**
 * Indicates whether there are active filters.
 * @returns {function({activeFilters: *}): boolean}
 */
const hasActiveFilters = () => ({ activeFilters }) => activeFilters.length > 0;

/**
 * Return whether there are filters available.
 * @returns {function(*=): boolean}
 */
const hasAvailableFilters = () => state => {
  const availableFilters = getAvailableFilters()(state);
  return availableFilters.length > 0;
};

/**
 * Return the filter selection operators for the provided filter key.
 * @param {string} key
 * @returns {function({filters?: *}): ([string]|*|[string])}
 */
const getFilterSelectionOperators = key => ({ filters }) => {
  const filter = findObjectInArray(filters, 'key', key);

  if (!filter) {
    return [];
  }

  if (Array.isArray(filter?.operators)) {
    return filter.operators;
  }

  return getFilterOperators(filter.type);
};

/**
 * Set the filter key of the filter selection.
 * @param {string} filterSelectionKey
 * @param {object} options
 * @returns {Function}
 */
const setFilterSelectionKey = (
  filterSelectionKey,
  options = { firstOperator: true, isCreate: false },
) => async state => {
  const { filterSelection, filters, setFilterSelection, loadFilterOptions } = state;

  try {
    const filter = findObjectInArray(filters, 'key', filterSelectionKey);

    if (!filter) {
      return;
    }

    const { currentKey, isNew } = filterSelection || {};
    const updatedFilterSelection = {
      ...(options.isCreate ? {} : filterSelection || {}),
      key: filterSelectionKey,
      type: filter.type,
      operator: null,
      value: null,
      filterOptions: filter.filterOptions || [],
      currentKey: options.isCreate ? null : currentKey || null,
      isNew: isNew || options.isCreate,
    };

    if (options.firstOperator) {
      const operators = getFilterSelectionOperators(filter.key)(state);
      const [firstOperator] = operators;

      updatedFilterSelection.operator = firstOperator || null;
    }

    if (loadFilterOptions) {
      updatedFilterSelection.filterOptions = await loadFilterOptions(filter);
    }

    setFilterSelection(updatedFilterSelection);
  } catch (err) {
    // Do something with the error.
  }
};

const getFilterTypeDefaultValue = type => {
  if (type === 'boolean') {
    return [false];
  }

  return null;
};

/**
 * Set the filter operator of the filter selection.
 * @param {string} operator
 * @returns {Function}
 */
const setFilterSelectionOperator = operator => ({ filterSelection, setFilterSelection }) => {
  const defaultValue = getFilterTypeDefaultValue(filterSelection?.type);
  const updatedFilterSelection = {
    ...filterSelection,
    operator,
    value: defaultValue,
  };

  setFilterSelection(updatedFilterSelection);
};

/**
 * Set the filter value of the filter selection.
 * @param {any} value
 * @returns {Function}
 */
const setFilterSelectionValue = value => ({ filterSelection, setFilterSelection }) => {
  const updatedFilterSelection = {
    ...filterSelection,
    value,
  };

  setFilterSelection(updatedFilterSelection);
};

/**
 * Create a new filter selection based on the first available filter.
 * @returns {Function}
 */
const createFilterSelection = key => async state => {
  try {
    const availableFilters = getAvailableFilters()(state);
    const [firstFilter] = availableFilters;
    let selectedFilter = firstFilter;

    if (key) {
      const requestedFilter = findObjectInArray(availableFilters, 'key', key);
      if (requestedFilter) {
        selectedFilter = requestedFilter;
      }
    }

    if (!selectedFilter) {
      return;
    }

    setFilterSelectionKey(selectedFilter.key, { firstOperator: true, isCreate: true })(state);
  } catch (err) {
    // Do something with error.
  }
};

/**
 * Return the name of a filter based on the provided filter key.
 * @param {string} filterKey
 * @returns {Function}
 */
const getFilterName = filterKey => state => {
  const { filters } = state;
  const filter = findObjectInArray(filters, 'key', filterKey);

  if (filter) {
    return filter.name;
  }

  return filterKey;
};

/**
 * Return the type of a filter based on the provided filter key.
 * @param {string} filterKey
 * @returns {Function}
 */
const getFilterType = filterKey => state => {
  const { filters } = state;
  const filter = findObjectInArray(filters, 'key', filterKey);

  if (filter) {
    return filter.type;
  }

  return undefined;
};

const setActiveFilters = activeFilters => state => {
  const { filters = [] } = state;
  const filterKeys = filters.map(filter => filter.key).filter(filterKey => filterKey);
  const validFilters = activeFilters.filter(activeFilter => filterKeys.includes(activeFilter.key));

  state.setActiveFilters(validFilters);
};

export default {
  getFilter,
  getFilterName,
  getFilterType,
  getActiveFilter,
  addActiveFilter,
  removeActiveFilter,
  updateActiveFilter,
  clearActiveFilters,
  createFilterSelection,
  clearFilterSelection,
  saveFilterSelection,
  getAvailableFilters,
  hasActiveFilters,
  hasAvailableFilters,
  setActiveFilters,
  setFilterSelectionKey,
  setFilterSelectionOperator,
  setFilterSelectionValue,
  getFilterSelectionOperators,
  getFixedFilter,
  loadFilterSelection,
};
